import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Tooltip } from 'antd';
import { InfoCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Viewer from 'components/DicomMediaViewer';
import Segments, { LabelTab, SegmentationPayload } from 'components/Labels/segments';
import Prediction from 'components/Predictions/predict';
import EncephalonSection from 'components/Encephalon';
import { useEncephalon } from 'contexts/EncephalonContext';
import { useGetEncephaloneStudyDataQuery } from 'services/dicomService';
import {
  useGetDicomStudyQuery,
  useGetStudyDetailQuery,
  useLazyGetDownloadLinkQuery,
  type DicomRow,
  type ReportSummary,
} from 'services/exploreService';
import { downloadStudyZip, saveFromUrl } from 'services/downloads';
import { Empty, Skeleton, ViewChips, annotationMarks, fmt, studyHref } from 'components/explore';
import { deriveExtension, measurementName, sectionName } from './labels';
import './dicompage.css';

export const dicomHref = (icid: string) => `/dicoms/${encodeURIComponent(icid)}`;

/** Router state carried by links into the DICOM page, so it can skip the study lookup. */
export type DicomNavState = { study?: string };

type KV = [string, React.ReactNode];
type MediaSource = { url: string; kind: 'video' | 'image' };

/**
 * Where a DICOM's rendering can be: multi-frame clips play the loop first; single-frame
 * DICOMs show the still image first (a one-frame MP4 shows as a black or frozen player).
 * Each source is tried in turn when the previous one fails to load.
 */
export function mediaSources(d: Pick<DicomRow, 'number_of_frames' | 'media'>): MediaSource[] {
  const { cine, still, poster } = d.media;
  const video = cine ? [{ url: cine, kind: 'video' as const }] : [];
  const images = [still, poster]
    .filter(Boolean)
    .map(url => ({ url: url as string, kind: 'image' as const }));
  return (d.number_of_frames ?? 0) > 1 ? [...video, ...images] : [...images, ...video];
}

/** Two-column descriptor table: label on the left, value on the right. */
const KVTable: React.FC<{ rows: KV[]; caption?: string }> = ({ rows, caption }) => (
  <table className="ax-dtable">
    {caption && <caption>{caption}</caption>}
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={k}>
          <th scope="row">{k}</th>
          <td>
            {v === null || v === undefined || v === '' ? <span className="ax-faint">–</span> : v}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Section: React.FC<{ title: string; extra?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  extra,
  children,
}) => (
  <section className="ax-dsection">
    <header>
      <h3>{title}</h3>
      {extra}
    </header>
    {children}
  </section>
);

const yes = (v: boolean | null | undefined) =>
  v ? <span style={{ color: 'var(--good)' }}>yes</span> : <span className="ax-faint">no</span>;

/** One DICOM in the study rail. */
const RailItem = React.memo<{
  d: DicomRow;
  current: boolean;
  onPick: (icid: string) => void;
  onSettled: (uuid: string) => void;
}>(({ d, current, onPick, onSettled }) => {
  const [srcIdx, setSrcIdx] = useState(0);
  const sources = [d.media.poster, d.media.still].filter(Boolean) as string[];
  const ref = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (current) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [current]);
  const exhausted = srcIdx >= sources.length;
  useEffect(() => {
    if (exhausted) onSettled(d.uuid);
  }, [exhausted, onSettled, d.uuid]);
  return (
    <button
      ref={ref}
      type="button"
      className={`ax-rail-dicom ${current ? 'sel' : ''}`}
      aria-current={current ? 'true' : undefined}
      onClick={() => onPick(d.icid)}
      title={d.icid}
    >
      <span className="thumb">
        {sources[srcIdx] ? (
          <img
            src={sources[srcIdx]}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onLoad={() => onSettled(d.uuid)}
            onError={() => setSrcIdx(i => i + 1)}
          />
        ) : (
          <span className="ph">{(d.number_of_frames ?? 0) > 1 ? 'cine' : 'still'}</span>
        )}
        {d.index != null && <b className="idx">#{d.index}</b>}
      </span>
      <span className="info">
        <span className="view">{d.labeled_view || d.predicted_view || 'No view'}</span>
        <span className="sub">
          {d.type ?? '–'} · {d.number_of_frames ?? '–'}f
          {annotationMarks(d).length > 0 && <> · {annotationMarks(d).join(' ')}</>}
        </span>
      </span>
    </button>
  );
});
RailItem.displayName = 'RailItem';

/** Narrative repeats the per-structure sections; older API versions still send it. */
const HIDDEN_SECTIONS = new Set(['narrative']);

/** Coded findings first, each distinct (region, condition, severity) once. */
export function displayFindings(findings: ReportSummary['findings']): ReportSummary['findings'] {
  const seen = new Set<string>();
  const out: ReportSummary['findings'] = [];
  for (const f of findings) {
    const coded = !!(f.region || f.condition);
    const key = coded
      ? `c|${(f.region ?? '').toLowerCase()}|${(f.condition ?? '').toLowerCase()}|${(f.stratification ?? '').toLowerCase()}`
      : `t|${(f.text || f.description || '').toLowerCase().replace(/\s+/g, ' ').trim()}`;
    if (key === 't|') continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(f);
  }
  return [
    ...out.filter(f => f.region || f.condition),
    ...out.filter(f => !(f.region || f.condition)),
  ];
}

const ReportTables: React.FC<{ report: ReportSummary }> = ({ report }) => {
  const [showSections, setShowSections] = useState(false);
  const findings = useMemo(() => displayFindings(report.findings), [report.findings]);
  const sections = report.sections.filter(s => !HIDDEN_SECTIONS.has(s.key));
  return (
    <>
      <Section
        title="Report"
        extra={
          <span className="ax-faint" style={{ fontSize: 12 }}>
            {[
              report.signer,
              report.date_created && new Date(report.date_created).toLocaleDateString(),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        }
      >
        {report.measurements.length ? (
          <table className="ax-dtable">
            <caption>Measurements</caption>
            <tbody>
              {report.measurements.map(m => (
                <tr key={m.key}>
                  <th scope="row">{measurementName(m.key)}</th>
                  <td>
                    {String(m.value)}
                    {m.unit && <span className="ax-faint"> {m.unit}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="ax-faint">No measurements recorded.</p>
        )}
      </Section>
      {findings.length > 0 && (
        <Section title={`Findings (${findings.length})`}>
          <table className="ax-dtable findings">
            <thead>
              <tr>
                <th>Region</th>
                <th>Condition</th>
                <th>Severity</th>
                <th aria-label="Source text" />
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => {
                const texts = f.texts?.length ? f.texts : [f.text || f.description].filter(Boolean);
                const coded = !!(f.region || f.condition);
                return (
                  <tr key={i}>
                    {coded ? (
                      <>
                        <td>{f.region ?? <span className="ax-faint">–</span>}</td>
                        <td>
                          {f.condition ? (
                            f.condition.replace(/_/g, ' ')
                          ) : (
                            <span className="ax-faint">–</span>
                          )}
                        </td>
                        <td>{f.stratification ?? <span className="ax-faint">–</span>}</td>
                      </>
                    ) : (
                      <td colSpan={3} className="ax-muted">
                        {texts[0]}
                      </td>
                    )}
                    <td className="info">
                      {coded && texts.length > 0 && (
                        <Tooltip
                          title={<span style={{ whiteSpace: 'pre-wrap' }}>{texts.join('\n')}</span>}
                        >
                          <InfoCircleOutlined
                            aria-label={`Report text: ${texts.join(' ')}`}
                            tabIndex={0}
                            className="ax-faint"
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}
      {sections.length > 0 && (
        <Section
          title="Report text"
          extra={
            <button type="button" className="ax-textbtn" onClick={() => setShowSections(s => !s)}>
              {showSections ? 'Hide' : `Show ${sections.length} sections`}
            </button>
          }
        >
          {showSections ? (
            <table className="ax-dtable text">
              <tbody>
                {sections.map(s => (
                  <tr key={s.key}>
                    <th scope="row">{sectionName(s.key)}</th>
                    <td>{s.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            sections.find(s => s.key === 'conclusions') && (
              <p className="ax-note" style={{ whiteSpace: 'pre-wrap' }}>
                {sections.find(s => s.key === 'conclusions')!.text}
              </p>
            )
          )}
        </Section>
      )}
    </>
  );
};

/** Thin progress bar: how many of the study's previews have loaded. */
const LoadBar: React.FC<{ done: number; total: number; label: string }> = ({
  done,
  total,
  label,
}) => {
  if (!total || done >= total) return null;
  const pct = Math.round((100 * done) / total);
  return (
    <div
      className="ax-loadbar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-label={label}
    >
      <div className="track">
        <i style={{ width: `${pct}%` }} />
      </div>
      <span>
        {label} {done}/{total}
      </span>
    </div>
  );
};

/** Counts loaded items per study; resets when the set of items changes. */
function useSettledCount(ids: string[]) {
  const key = ids.join(',');
  const [settled, setSettled] = useState<{ key: string; ids: Set<string> }>({
    key,
    ids: new Set(),
  });
  const onSettled = useCallback(
    (id: string) =>
      setSettled(prev => {
        const base = prev.key === key ? prev.ids : new Set<string>();
        if (base.has(id)) return prev.key === key ? prev : { key, ids: base };
        const next = new Set(base);
        next.add(id);
        return { key, ids: next };
      }),
    [key]
  );
  return { done: settled.key === key ? settled.ids.size : 0, onSettled };
}

/**
 * One DICOM in its study: the study's DICOMs on the left (←/→ to move), the clip with
 * overlays in the middle with labels and predictions below it, descriptor tables on the right.
 *
 * Everything shown comes from the study detail (one cached request per study), so moving
 * between DICOMs of a study, or opening one from the study page, needs no new request.
 */
const DicomPage: React.FC = () => {
  const { icid = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateStudy = (location.state as DicomNavState | null)?.study;

  // Which study is this DICOM in? Links from Atlas pass it along; otherwise look it up.
  const lookup = useGetDicomStudyQuery(icid, { skip: !icid || !!stateStudy });
  const studyKey =
    stateStudy ?? lookup.currentData?.study_icid ?? lookup.currentData?.study_uuid ?? '';
  const studyQuery = useGetStudyDetailQuery(studyKey, { skip: !studyKey });
  // currentData, not data: data keeps the previous study while the next one loads, which
  // used to leave the old DICOM, rail and links on screen.
  const detail = studyQuery.currentData;
  const lookupError = !stateStudy && lookup.isError;

  const [selectedSegmentation, setSelectedSegmentation] = useState<SegmentationPayload | null>(
    null
  );
  const [selectedKeypointFrame, setSelectedKeypointFrame] = useState<null | {
    frameUrl: string;
    frameIcid: string;
    frameExtension: string;
    keypoints: any[];
  }>(null);
  const [sourceIdx, setSourceIdx] = useState(0);
  // Labels sit below the viewer by default. While exploring them, "Show beside viewer" moves
  // the list next to the frame; "Move below" puts it back. Kept while stepping through the
  // study's DICOMs, not remembered across visits.
  const [labelsSide, setLabelsSide] = useState(false);
  const [labelsBackBelow, setLabelsBackBelow] = useState(false);
  const labelsTab = useRef<LabelTab>('segmentations');
  const stageRef = useRef<HTMLDivElement | null>(null);
  // Opening the side panel usually happens with the page scrolled down to the list; bring
  // the frame back into view so both are on screen.
  useEffect(() => {
    if (labelsSide) stageRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [labelsSide]);
  const toggleLabelsSide = useCallback(() => {
    setLabelsSide(side => {
      if (side) setLabelsBackBelow(true);
      return !side;
    });
  }, []);

  useEffect(() => {
    setSelectedSegmentation(null);
    setSelectedKeypointFrame(null);
    setSourceIdx(0);
  }, [icid]);

  const rail = useMemo(() => detail?.dicoms ?? [], [detail]);
  const pos = rail.findIndex(d => d.icid === icid);
  const row = pos >= 0 ? rail[pos] : undefined;
  const labelsProps = row
    ? {
        mediaUuid: row.media_uuid ?? '',
        dicomId: row.uuid as unknown as number,
        selectedIcid: selectedSegmentation?.maskIcid ?? selectedKeypointFrame?.frameIcid ?? null,
        onSegmentationClick: (p: SegmentationPayload) => {
          setSelectedSegmentation(p);
          setSelectedKeypointFrame(null);
        },
        onKeypointClick: (p: any) => {
          setSelectedKeypointFrame({ ...p });
          setSelectedSegmentation(null);
        },
        onToggleSide: toggleLabelsSide,
        defaultTab: labelsTab.current,
        onTabChange: (t: LabelTab) => {
          labelsTab.current = t;
        },
      }
    : null;
  const navStudy = detail ? (detail.study.icid ?? detail.study.uuid) : undefined;
  const go = useCallback(
    (target?: string) =>
      target && navigate(dicomHref(target), { state: navStudy ? { study: navStudy } : undefined }),
    [navigate, navStudy]
  );
  const prev = pos > 0 ? rail[pos - 1].icid : undefined;
  const next = pos >= 0 && pos < rail.length - 1 ? rail[pos + 1].icid : undefined;

  const railIds = useMemo(() => rail.map(d => d.uuid), [rail]);
  const { done: previewsLoaded, onSettled } = useSettledCount(railIds);

  // Warm the neighbours' media so stepping through the study starts playing at once.
  useEffect(() => {
    [prev, next].forEach(t => {
      const d = rail.find(x => x.icid === t);
      const src = d ? mediaSources(d)[0] : undefined;
      if (src?.kind === 'image') new Image().src = src.url;
      else if (src?.kind === 'video') {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = src.url;
        document.head.appendChild(link);
        setTimeout(() => link.remove(), 30000);
      }
    });
  }, [prev, next, rail]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        if (prev) {
          e.preventDefault();
          go(prev);
        }
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        if (next) {
          e.preventDefault();
          go(next);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, go]);

  // Encephalon export state follows the study, as on the previous page.
  const { setStudyExists, setScans, setCurrentStudyUuid, currentStudyUuid, triggerScanUpdate } =
    useEncephalon();
  const studyIcid = detail?.study.icid ?? '';
  const { currentData: encephalonStudy } = useGetEncephaloneStudyDataQuery(studyIcid, {
    skip: !studyIcid,
  });
  useEffect(() => {
    const uuid = encephalonStudy?.results?.[0]?.uuid || null;
    if (uuid && uuid !== currentStudyUuid) {
      setStudyExists(true);
      setCurrentStudyUuid(uuid);
      triggerScanUpdate();
    }
    if (!uuid && currentStudyUuid) {
      setStudyExists(false);
      setCurrentStudyUuid(null);
      setScans([]);
    }
  }, [
    encephalonStudy,
    currentStudyUuid,
    setStudyExists,
    setCurrentStudyUuid,
    setScans,
    triggerScanUpdate,
  ]);

  const sources = useMemo(() => (row ? mediaSources(row) : []), [row]);
  const source = sources[sourceIdx];
  const media = useMemo(() => {
    if (selectedKeypointFrame)
      return {
        signed_url: selectedKeypointFrame.frameUrl,
        extension: selectedKeypointFrame.frameExtension,
        icid: selectedKeypointFrame.frameIcid,
      };
    if (selectedSegmentation)
      return {
        signed_url: selectedSegmentation.frameUrl,
        extension: selectedSegmentation.frameExtension,
        icid: selectedSegmentation.frameIcid,
      };
    if (!source) return null;
    const ext = deriveExtension(source.url) || (source.kind === 'video' ? 'mp4' : 'png');
    return { signed_url: source.url, extension: ext, icid };
  }, [selectedKeypointFrame, selectedSegmentation, source, icid]);

  const [getDownloadLink] = useLazyGetDownloadLinkQuery();
  const downloadFile = useCallback(
    async (kind: 'dicom' | 'video' | 'image') => {
      try {
        const { url, filename } = await getDownloadLink({ icid, kind }, true).unwrap();
        saveFromUrl(url, filename);
        toast.success(`Downloading ${filename}`, { autoClose: 3000 });
      } catch (e: any) {
        const msg = e?.data?.detail || e?.data?.message || 'Download failed';
        toast.error(typeof msg === 'string' ? msg : 'Download failed');
      }
    },
    [getDownloadLink, icid]
  );
  const downloadStudy = useCallback(async () => {
    if (!detail) return;
    toast.info(
      `Downloading ${fmt(rail.length)} DICOMs as a ZIP; it appears in your downloads as it streams.`,
      {
        autoClose: 6000,
      }
    );
    await downloadStudyZip(detail.study.uuid);
  }, [detail, rail.length]);

  const notFound =
    lookupError ||
    (studyQuery.isError && !detail) ||
    (!!detail && pos < 0 && !studyQuery.isFetching);
  if (notFound) {
    return (
      <div className="ax-page">
        <Alert
          type="warning"
          showIcon
          message={`DICOM ${icid} not found`}
          description="Check the ICID, or find it from the Studies or DICOMs pages."
        />
      </div>
    );
  }

  const study = detail?.study;
  const loadingStudy = !detail;
  const reportNumber = (key: string): number | undefined => {
    const mm = detail?.report?.measurements.find(x => x.key === key);
    const n = mm ? Number(mm.value) : NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const dicomRows: KV[] = row
    ? [
        ['ICID', <span className="ax-mono">{row.icid}</span>],
        ['Index in study', row.index],
        ['Type', row.type],
        ['Manufacturer', row.manufacturer],
        ['Model', row.model],
        ['Frames', fmt(row.number_of_frames)],
        [
          'Frame rate',
          row.frames_per_second ? `${Number(row.frames_per_second).toFixed(1)} fps` : null,
        ],
        [
          'Size',
          row.pixel_width && row.pixel_height
            ? `${row.pixel_width} × ${row.pixel_height} px`
            : null,
        ],
        [
          'Pixel spacing',
          row.physical_delta_x
            ? `${Number(row.physical_delta_x).toFixed(4)} × ${Number(row.physical_delta_y ?? 0).toFixed(4)} ${row.physical_units_x_direction ?? ''}`.trim()
            : null,
        ],
        [
          'AI view',
          row.predicted_view
            ? `${row.predicted_view}${row.predicted_confidence != null ? ` (${(100 * row.predicted_confidence).toFixed(0)}%)` : ''}`
            : null,
        ],
        ['Tech view', row.labeled_view],
        ['Segmentation', yes(row.has_segmentation)],
        ['Keypoints', yes(row.has_keypoints)],
        ['Quality label', yes(row.has_quality_label)],
        [
          'Annotation',
          row.skipped_annotations ? <span style={{ color: 'var(--warn)' }}>skipped</span> : null,
        ],
        ['Added', row.created_at ? new Date(row.created_at).toLocaleString() : null],
      ]
    : [];
  const studyRows: KV[] = study
    ? [
        [
          'Study',
          <Link to={studyHref(study.icid ?? study.uuid)} className="ax-link">
            {study.icid ?? study.uuid}
          </Link>,
        ],
        ['Source', study.datasource],
        ['Foreign id', study.foreign_id],
        ['Visit year', study.visit_year],
        ['Age at visit', study.age_at_visit],
        ['Sex', study.gender],
        ['Race / ethnicity', [study.race, study.ethnicity].filter(Boolean).join(' / ')],
        [
          'DICOMs',
          `${fmt(rail.length)}${study.number_of_usable_dicoms != null ? ` (${fmt(study.number_of_usable_dicoms)} usable)` : ''}`,
        ],
        ['Designation', study.designation],
      ]
    : [];

  return (
    <div className="ax-dicompage">
      <aside className="ax-studyrail" aria-label="DICOMs in this study">
        <div className="head">
          {study ? (
            <>
              <Link to={studyHref(study.icid ?? study.uuid)} className="ax-link">
                {study.icid}
              </Link>
              <span className="ax-faint">
                {fmt(rail.length)} DICOMs{study.datasource ? ` · ${study.datasource}` : ''}
              </span>
              <LoadBar done={previewsLoaded} total={rail.length} label="Previews" />
            </>
          ) : (
            <div className="ax-loading-note" role="status">
              <span className="ax-spinner" aria-hidden />
              Loading study…
            </div>
          )}
        </div>
        <div className="list" role="list">
          {loadingStudy
            ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={58} />)
            : rail.map(d => (
                <RailItem
                  key={d.uuid}
                  d={d}
                  current={d.icid === icid}
                  onPick={go}
                  onSettled={onSettled}
                />
              ))}
        </div>
      </aside>

      <div className="ax-dcenter">
        <div className="ax-dtoolbar">
          <Tooltip title="Previous DICOM (←)">
            <Button
              icon={<LeftOutlined />}
              disabled={!prev}
              onClick={() => go(prev)}
              aria-label="Previous DICOM"
            />
          </Tooltip>
          <div className="title">
            <span className="ax-mono">{icid}</span>
            <span className="ax-faint">
              {pos >= 0 ? `${pos + 1} of ${rail.length} in study` : ''}
            </span>
          </div>
          <Tooltip title="Next DICOM (→)">
            <Button
              icon={<RightOutlined />}
              disabled={!next}
              onClick={() => go(next)}
              aria-label="Next DICOM"
            />
          </Tooltip>
          {row && (
            <span className="chips">
              <ViewChips
                predicted={row.predicted_view}
                labeled={row.labeled_view}
                confidence={row.predicted_confidence}
              />
            </span>
          )}
        </div>

        <div ref={stageRef} className={`ax-dstage${labelsSide && row ? ' with-dock' : ''}`}>
          <div className="ax-dviewer">
            {media ? (
              <Viewer
                key={selectedKeypointFrame ? `kp-${selectedKeypointFrame.frameIcid}` : `v-${icid}`}
                mediaData={media}
                onMediaError={
                  selectedKeypointFrame || selectedSegmentation
                    ? undefined
                    : () => setSourceIdx(i => i + 1)
                }
                onDownloadFile={downloadFile}
                segmentationData={
                  selectedSegmentation
                    ? {
                        icid: selectedSegmentation.maskIcid,
                        paths: selectedSegmentation.paths,
                        frameUrl: selectedSegmentation.frameUrl,
                      }
                    : null
                }
                onClearSegmentation={() => setSelectedSegmentation(null)}
                dicom_Id={icid}
                keypoints={selectedKeypointFrame ? selectedKeypointFrame.keypoints : []}
                onClearKeypoints={() => setSelectedKeypointFrame(null)}
                numberOfFrames={row?.number_of_frames ?? undefined}
                onDownloadFullStudy={detail ? downloadStudy : undefined}
                isDownloadingFullStudy={false}
              />
            ) : loadingStudy ? (
              <div className="ax-viewer-loading" role="status">
                <span className="ax-spinner" aria-hidden />
                Loading DICOM…
              </div>
            ) : (
              <Empty title="No rendered media for this DICOM">
                <button type="button" className="ax-textbtn" onClick={() => downloadFile('dicom')}>
                  Download the original DICOM
                </button>
              </Empty>
            )}
          </div>

          {labelsSide && labelsProps && (
            <div className="ax-ddock">
              <div className="inner">
                <Segments layout="dock" defaultExpanded {...labelsProps} />
              </div>
            </div>
          )}
        </div>

        {row && (
          <div className="ax-dpanels">
            {!labelsSide && labelsProps && (
              <Segments defaultExpanded={labelsBackBelow} {...labelsProps} />
            )}
            <Prediction dicomId={row.uuid as unknown as number} defaultExpanded={false} />
          </div>
        )}
      </div>

      <aside className="ax-dside" aria-label="Details">
        <Section title="DICOM">{row ? <KVTable rows={dicomRows} /> : <Skeleton h={200} />}</Section>
        <Section title="Study">
          {study ? <KVTable rows={studyRows} /> : <Skeleton h={120} />}
        </Section>
        {detail &&
          (detail.report ? (
            <ReportTables report={detail.report} />
          ) : (
            <Section title="Report">
              <p className="ax-faint">No report in the warehouse for this study.</p>
            </Section>
          ))}
        {study?.icid && (
          <Section title="Encephalon">
            <EncephalonSection
              name={study.icid}
              age={
                reportNumber('age') ??
                (study.age_at_visit ? Number(study.age_at_visit) : (undefined as unknown as number))
              }
              height={reportNumber('height') as number}
              weight={reportNumber('weight') as number}
              sex={(study.gender ?? '') as string}
              studyUuid={study.uuid}
            />
          </Section>
        )}
      </aside>
      <ToastContainer position="top-right" autoClose={5000} newestOnTop theme="dark" />
    </div>
  );
};

export default DicomPage;
